<template>
	<div class="manage-users">
		<h2 class="content-block">Users Management</h2>

		<dx-data-grid style="width:100%;"
			class="dx-card wide-card"
			ref="dataGridRef"
			:data-source="dataSourceConfig"
			:focused-row-index="0"
			:show-borders="false"
			:focused-row-enabled="true"
			:column-auto-width="false"
			:column-hiding-enabled="true"
			:allowColumnResizing="true" columnResizingMode="widget"
			@exporting="onExporting"
		>
			<dx-state-storing :enabled="false" type="localStorage" storage-key="manage-employees" />
			<dx-column-chooser :enabled="true" mode="select" :searchEnabled="true" height="400px">
				<dx-position my="right top" at="right bottom" of=".dx-datagrid-column-chooser-button" />
			</dx-column-chooser>
			<dx-paging :page-size="10" />
			<dx-pager :show-page-size-selector="true" :show-info="true" />
			<dx-filter-row :visible="true" />
			<dx-header-filter :visible="true" />
			<dx-search-panel :visible="true" width="300px" />
			<dx-toolbar>
				<dx-item location="before" name="searchPanel" />
				<dx-item location="before" name="addRowButton" css-class="force-primary-color" />
				<dx-item location="after" name="dateLengthButton">
					<dx-button icon="refresh" hint="Refresh data" @click="onRefresh" />
				</dx-item>
				<dx-item name="exportButton" location="after" />
				<dx-item name="columnChooserButton" location="after" />
			</dx-toolbar>
			<dx-scrolling :useNative="true" />
			<dx-export :enabled="true" :formats="['xlsx', 'pdf']" />
			<dx-editing :allow-adding="true" :allow-updating="true" mode="form" :allow-deleting="true"></dx-editing>

			<dx-column data-field="id" data-type="number" caption="ID" :width="120" :allow-header-filtering="false" sort-order="asc" :sort-index="0">
				<DxFormItem :visible="false" />
			</dx-column>
			<dx-column data-field="name" caption="Name" :allow-header-filtering="false">
				<DxFormItem :visible="true" />
			</dx-column>
			<dx-column data-field="roles" caption="Roles" :allow-header-filtering="true" cell-template="rolesCellTemplate">
				<DxFormItem :visible="true" editor-type="dxTagBox" :editor-options="rolesTagEditorOptions" />
			</dx-column>
			<template #rolesCellTemplate="{ data }">
				<div class="roles-cell" :title="getDisplayRoles(data).join(', ')">
					<template v-if="getDisplayRoles(data).length">
						<span
							v-for="roleName in getDisplayRoles(data)"
							:key="roleName"
							class="role-badge"
							:style="getRoleBadgeStyle(roleName)"
						>{{ roleName }}</span>
					</template>
					<span v-else class="role-badge role-badge--empty">No roles</span>
				</div>
			</template>
			<dx-column data-field="email" caption="Email" :allow-header-filtering="false">
				<DxFormItem :visible="true" />
			</dx-column>
			<dx-column data-field="password" caption="Password" :allow-header-filtering="false" :visible="false">
				<DxFormItem :visible="true" :editor-options="{ placeholder: 'Min 8-characters required' }" />
			</dx-column>
			<dx-column data-field="last_access" data-type="datetime" caption="Last activity" :width="160" :allow-header-filtering="false">
				<DxFormItem :visible="false" />
			</dx-column>
			<dx-column data-field="created_at" data-type="datetime" caption="User created" :width="160" :allow-header-filtering="false">
				<DxFormItem :visible="false" />
			</dx-column>
		</dx-data-grid>
	</div>
</template>

<script setup>
import CustomStore from 'devextreme/data/custom_store';
import DxDataGrid, { DxColumn, DxFilterRow, DxPager, DxPaging, DxColumnChooser, DxPosition, DxStateStoring, DxToolbar, DxItem, DxSearchPanel, DxScrolling, DxHeaderFilter, DxExport, DxEditing, DxFormItem } from "devextreme-vue/data-grid";
import { DxButton } from 'devextreme-vue/button';
import { inject, ref, computed } from "vue";
import notify from 'devextreme/ui/notify';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { jsPDF } from 'jspdf';
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter';

const $http = inject('$http');
const dataGridRef = ref(null);
const rolesList = ref([]);

const roleNamePattern = /^[A-Za-z0-9+-]+$/;
const rolesTagEditorOptions = computed(() => ({
	// Data and lookup
	dataSource: rolesList.value,
	displayExpr: 'name',
	valueExpr: 'name',
	// UX
	searchEnabled: true,
	searchExpr: 'name',
	showSelectionControls: false,
	applyValueMode: 'instantly',
	placeholder: 'Type to add a new role or select existing',
	noDataText: 'No matches. Press Enter to create this role',
	// Allow creating new roles on the fly
	acceptCustomValue: true,
	onCustomItemCreating: (e) => {
		const typedText = String(e.text || '').trim();
		if (!typedText) {
			// Nothing to create
			e.customItem = null;
			return;
		}
		if (!roleNamePattern.test(typedText)) {
			// Invalid role name; reject creation
			e.customItem = null;
			return;
		}
		// Disallow creating a reserved role name: 'admin' (case-insensitive)
		if (typedText.toLowerCase() === 'admin') {
			notify({
				message: "The 'admin' role is reserved and cannot be created.",
				type: 'warning',
				displayTime: 2500,
				position: 'top right',
			});
			e.customItem = null;
			return;
		}
		// Avoid duplicates (case-insensitive)
		const existing = rolesList.value.find(r => String(r?.name || '').toLowerCase() === typedText.toLowerCase());
		if (existing) {
			e.customItem = existing;
			return;
		}
		const newItem = { name: typedText };
		rolesList.value.push(newItem);
		e.customItem = newItem;
	},
}));

const dataSourceConfig = new CustomStore({
	key: 'id',
	loadMode: 'raw',
	remoteOperations: false,
	load: () => {
		// Get a list of all users and roles.
		return Promise.all([
			$http.get('/users'),
			$http.get('/role')
		]).then(([usersResponse, rolesResponse]) => {
			rolesList.value = rolesResponse.data;
			return usersResponse.data;
		});
	},
	insert: (values) => {
		return $http.post('/users', values).then(response => {
			return response.data;
		});
	},
	update: (key, values) => {
		return $http.patch(`/users/${key}`, values).then(response => {
			return response.data;
		});
	},
});

const onExporting = (e) => {
	let dateCode = (new Date()).toISOString().substring(0, 10);
	if (e.format == 'xlsx') {
		const workbook = new Workbook();
		const worksheet = workbook.addWorksheet('Main sheet');
		exportDataGrid({
			component: e.component,
			worksheet: worksheet,
			customizeCell: function(options) {
				options.excelCell.font = { name: 'Arial', size: 12 };
				options.excelCell.alignment = { horizontal: 'left' };
			} 
		}).then(function() {
			workbook.xlsx.writeBuffer()
				.then(function(buffer) {
					saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Employees-' + dateCode + '.xlsx');
				});
		});
	} else if (e.format == 'pdf') {
		const doc = new jsPDF();
		exportDataGridToPdf({
			jsPDFDocument: doc,
			component: e.component
		}).then(() => {
			doc.save('Employees-' + dateCode + '.pdf');
		});
	}
};

const onRefresh = () => {
	const gridInstance = dataGridRef.value?.instance;
	if (!gridInstance) return;
	const dataSource = gridInstance.getDataSource?.();
	if (dataSource?.reload) {
		dataSource.reload();
	} else {
		gridInstance.refresh?.();
	}
};

// Deterministic pastel color styles for role badges
const hashStringToHue = (input) => {
	const text = String(input || '');
	let hash = 0;
	for (let i = 0; i < text.length; i++) {
		hash = text.charCodeAt(i) + ((hash << 5) - hash);
	}
	return Math.abs(hash) % 360;
};

const getRoleBadgeStyle = (roleName) => {
	if (roleName.toLowerCase() === 'admin') {
		return {
			backgroundColor: '#ffffff',
			borderColor: '#cc0000',
			color: '#ee0000',
		};
	}
	const hue = hashStringToHue(roleName);
	return {
		backgroundColor: `hsla(${hue}, 70%, 92%, 1)`,
		borderColor: `hsla(${hue}, 60%, 75%, 1)`,
		color: `hsla(${hue}, 35%, 25%, 1)`,
	};
};

// Build the roles to show in view mode. Inject ADMIN for user id 1.
const getDisplayRoles = (cellData) => {
	// cellData has { data: rowData, value: cellValue, ... }
	const row = cellData?.data || {};
	const roles = Array.isArray(cellData?.value) ? [...cellData.value] : [];
	if (row?.id === 1) {
		// Add ADMIN visually (uppercased for emphasis)
		roles.unshift('ADMIN');
	}
	return roles;
};

</script>

<style lang="scss">
.manage-users {
	.roles-cell {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		align-items: center;
	}
	.role-badge {
		display: inline-flex;
		align-items: center;
		padding: 2px 8px;
		border: 1px solid transparent;
		border-radius: 9999px;
		font-size: 12px;
		line-height: 18px;
		white-space: nowrap;
	}
	.role-badge--empty {
		background-color: #f1f5f9;
		border-color: #e2e8f0;
		color: #64748b;
	}
}
</style>
